import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../components/CustomDataGridV2";
import { GlobalTableHeightWrapper } from "../../../../../../components/GlobalTableHeightWrapper";
import Text from "../../../../../../components/Text";
import { useInsuranceAccountReceivable } from "../../../../../../queries/account-receivables/use-insurance-account-receivable";
import { useAccountReceivableTotal } from "../../../../../../queries/account-receivables/use-account-receivable-totals";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  getTrimmedUuid,
  getXtenantId,
} from "../../../../../../utils/utils";
import {
  AccountReceivableFilterType,
  formatCurrency,
  toSafeIsoString,
  UPPER_TABLE_COLUMNS,
} from "../account-receivable-constant";

type AccountReceivableOutletContext = {
  search: string;
  setSearch: (search: string) => void;
  filter: AccountReceivableFilterType;
  setFilter: Dispatch<SetStateAction<AccountReceivableFilterType>>;
};

export type InsuranceAccountReceivableRow = {
  payerId?: string;
  payerName?: string;
  duration0To30?: number;
  duration31To60?: number;
  duration61To90?: number;
  duration91To120?: number;
  duration120Plus?: number;
  total?: number;
};

const AccountReceivableInsuranceTable = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { search, filter } = useOutletContext<AccountReceivableOutletContext>();
  // const navigate = useNavigate();

  const startDate = toSafeIsoString(filter?.startDate);
  const endDate = toSafeIsoString(filter?.endDate);
  const searchString = search || undefined;

  const { data: listResponse, isLoading: isListLoading } =
    useInsuranceAccountReceivable({
      pagination,
      gridSorting,
      searchString,
      startDate,
      endDate,
      xTENANTID: getXtenantId(),
    });

  const { data: totalsResponse, isLoading: isTotalsLoading } =
    useAccountReceivableTotal({
      searchString,
      startDate,
      endDate,
      payerType: "INSURANCE",
      xTENANTID: getXtenantId(),
    });

  const COLUMNS = applyDefaultColumnSettings<InsuranceAccountReceivableRow>(
    [
      {
        field: "payerId",
        flex: 0.5,
        minWidth: 100,
        headerName: "Payer ID",
        renderCell: ({ value }) => (
          <Text isTabletext>{getTrimmedUuid(value) ?? "-"}</Text>
        ),
      },
      {
        field: "payerName",
        flex: 1,
        minWidth: 200,
        headerName: "Payer Name",
        renderCell: ({ value }) => <Text isTabletext>{value ?? "-"}</Text>,
      },
      {
        field: "duration0To30",
        flex: 0.5,
        minWidth: 110,
        headerName: "0-30 Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "duration31To60",
        flex: 0.5,
        minWidth: 110,
        headerName: "31-60 Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "duration61To90",
        flex: 0.5,
        minWidth: 110,
        headerName: "61-90 Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "duration91To120",
        flex: 0.5,
        minWidth: 110,
        headerName: "91-120 Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "duration120Plus",
        flex: 0.5,
        minWidth: 110,
        headerName: "120+ Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "total",
        flex: 0.5,
        minWidth: 100,
        headerName: "Total",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
    ],
    ["payerName", "total"]
  );

  const topColumnHeight = 80;

  return (
    <Box>
      <Box height={`${topColumnHeight}px`}>
        <CustomDataGridV2
          rows={totalsResponse?.data ? [totalsResponse?.data] : []}
          hideFooter
          rowCount={totalsResponse?.data ? 1 : 0}
          getRowId={() => "totals"}
          rowHeight={40}
          columns={UPPER_TABLE_COLUMNS}
          loading={isTotalsLoading}
        />
      </Box>

      <GlobalTableHeightWrapper
        height={`calc(100vh - ${topColumnHeight + 168}px)`}
      >
        <CustomDataGridV2
          searchString={searchString}
          loading={isListLoading}
          rows={listResponse?.data.content ?? []}
          getRowId={(row) => row?.payerId ?? ""}
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          rowCount={listResponse?.data?.page?.totalElements}
          disableRowSelectionOnClick
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default AccountReceivableInsuranceTable;
